import { Stack } from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { SetRequired } from 'type-fest';

import { Button } from '@/components/form/baseInputs/Button';
import {
  ButtonWithPopover,
  ButtonWithPopoverProps,
} from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { EyeIcon } from '@/components/icons/EyeIcon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { AISuggestionsCountBadge } from '@/modules/aiSuggestions/components/AISuggestionsCountBadge';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getSectionOptions } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

import { useEditEntityTrowser } from './hooks/useEditEntityTrowser';

export interface EditEntitySplitScreenOpenModalButtonProps
  extends Partial<ButtonWithPopoverProps> {
  // initialSection: If given one initial section, we won't show the popover button.
  initialSection?: EditEntitySection;
  // initialSectionOptions: If given initial section options, we'll show those in
  // the popover menu, otherwise, we'll show all supported sections for the entity type.
  initialSectionOptions?: EditEntitySection[];
  buttonText?: ReactNode;
  // openOnMount: If true, open the EditEntitySplitScreenModal immediately to
  // the BasicInfo section immediately on mount.
  openOnMount?: boolean;
  // badgeText: If provided, will show a badge with this text on the button.
  badgeText?: string;
}

export const EditEntitySplitScreenOpenModalButton = ({
  initialSection,
  initialSectionOptions,
  buttonText: buttonTextExternal,
  openOnMount,
  badgeText,
  ...buttonProps
}: EditEntitySplitScreenOpenModalButtonProps) => {
  const canEdit = useCanEditHouseholdData();
  const { householdId, primaryClients } = useHouseholdDetailsContext();
  const { entityId, entityType } = useEntityDetailsContext();

  const buttonText =
    buttonTextExternal ?? (canEdit ? 'Edit details' : 'View details');

  const { openForEntity } = useEditEntityTrowser();

  const editEntitySections = useMemo(() => {
    return getSectionOptions(entityType, primaryClients);
  }, [entityType, primaryClients]);

  const handleOpenModalToSection = useCallback(
    (section: EditEntitySection) => {
      if (!entityType || !entityId) return;
      openForEntity(entityId, { section });
    },
    [openForEntity, entityId, entityType]
  );

  useEffect(() => {
    if (openOnMount && entityType && entityId) {
      openForEntity(entityId, {
        section: initialSection ?? EditEntitySection.BASIC_INFORMATION,
      });
    }
  }, [openOnMount, initialSection, entityType, entityId, openForEntity]);

  return (
    <OpenModalButton
      entityType={entityType}
      initialSection={initialSection}
      editEntitySections={
        initialSectionOptions
          ? getSectionOptions(entityType, primaryClients, initialSectionOptions)
          : editEntitySections
      }
      openModalToSection={handleOpenModalToSection}
      disabled={!entityId || !householdId}
      buttonText={buttonText}
      badgeText={badgeText}
      variant="secondary"
      {...buttonProps}
    />
  );
};

interface OpenModalButtonProps
  extends SetRequired<
    Omit<Partial<ButtonWithPopoverProps>, 'onMouseLeave'>,
    'variant'
  > {
  entityType: EntityType | null;
  // If given one initial section, we won't show the popover button.
  initialSection?: EditEntitySection;
  editEntitySections: { label: string; section: EditEntitySection }[];
  openModalToSection: (section: EditEntitySection) => void;
  disabled: boolean;
  buttonText: ReactNode;
  badgeText?: string;
}

/**
 * If there's only one edit section, show a regular button that opens the modal
 * directly to that section. If there are multiple sections, show a button with
 * popover that allows the user to select the section to open the modal to.
 */
const OpenModalButton = ({
  entityType,
  initialSection,
  editEntitySections,
  openModalToSection,
  disabled,
  buttonText,
  badgeText,
  size = 'sm',
  ...buttonProps
}: OpenModalButtonProps) => {
  const canEdit = useCanEditHouseholdData();
  const Icon = canEdit ? Edit02Icon : EyeIcon;

  const buttonContent = useMemo(() => {
    if (!badgeText) {
      return buttonText;
    }
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        {buttonText}
        <Badge variant={BadgeVariants.Success} display={badgeText} />
      </Stack>
    );
  }, [badgeText, buttonText]);

  if (initialSection) {
    return (
      <Button
        onClick={() => openModalToSection(initialSection)}
        startIcon={Icon}
        disabled={disabled}
        size={size}
        {...buttonProps}
      >
        {buttonText}
      </Button>
    );
  }

  return (
    <ButtonWithPopover
      startIcon={Icon}
      label={buttonContent}
      popperVariant="menuBelow"
      size={size}
      {...buttonProps}
    >
      {editEntitySections.map(({ label, section }) => (
        <MenuItem
          key={section}
          label={label}
          iconAfter={
            <AISuggestionsCountBadge
              entityType={entityType}
              section={section}
            />
          }
          onClick={() => openModalToSection(section)}
        />
      ))}
    </ButtonWithPopover>
  );
};
